import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Target, Megaphone, Package, Users, GitFork, Globe,
  FlaskConical, ScrollText, Settings, LogOut, Sparkles, AlertTriangle, LayoutGrid,
  ShoppingBag, Coins, TrendingUp, Truck, Pencil, Mail,
  Sun, GitPullRequest,
  Radar, Goal, Network, Calculator, Lightbulb,
  type LucideIcon,
  Store,
  FileText,
  KeyRound,
  Boxes,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../api/client.js";

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  group?: "main" | "marketing" | "direct" | "pim" | "market" | "analytics" | "system";
}

const NAV_ITEMS: NavItem[] = [
  { path: "/scoreboard", label: "Scoreboard", icon: Target, group: "main" },
  { path: "/media-plan", label: "Медиа-план", icon: LayoutGrid, group: "main" },
  { path: "/", label: "Дашборд", icon: LayoutDashboard, group: "main" },
  { path: "/campaigns", label: "Кампании", icon: Megaphone, group: "main" },
  { path: "/marketing/today", label: "Сегодня", icon: Sun, group: "marketing" },
  { path: "/marketing/experiments", label: "Эксперименты", icon: FlaskConical, group: "marketing" },
  { path: "/marketing/decisions", label: "Журнал решений", icon: ScrollText, group: "marketing" },
  { path: "/marketing/changes", label: "Изменения", icon: GitPullRequest, group: "marketing" },
  { path: "/direct/today", label: "Сегодня", icon: Radar, group: "direct" },
  { path: "/direct/hypotheses", label: "Гипотезы", icon: Lightbulb, group: "direct" },
  { path: "/direct/goals", label: "Цели", icon: Goal, group: "direct" },
  { path: "/direct/experiments", label: "Эксперименты", icon: Network, group: "direct" },
  { path: "/direct/planner", label: "Конструктор", icon: Calculator, group: "direct" },
  { path: "/pim/stock", label: "Остатки", icon: Boxes, group: "pim" },
  { path: "/pim/market", label: "Товары на Маркете", icon: Store, group: "market" },
  { path: "/pim/market/content", label: "Контент и кадры", icon: FileText, group: "market" },
  { path: "/pim/market/requests", label: "Заявки в кабинет", icon: GitPullRequest, group: "market" },
  { path: "/sales", label: "Обзор продаж", icon: ShoppingBag, group: "analytics" },
  { path: "/sales-ads", label: "Реклама сайта", icon: TrendingUp, group: "analytics" },
  { path: "/pnl", label: "Юнит-экономика", icon: Coins, group: "analytics" },
  { path: "/buyout", label: "Товарооборот сайта", icon: Truck, group: "analytics" },
  { path: "/catalog", label: "Каталог", icon: Pencil, group: "analytics" },
  { path: "/skus", label: "Товары", icon: Package, group: "analytics" },
  { path: "/cohorts", label: "Когорты / LTV", icon: Users, group: "analytics" },
  { path: "/insights", label: "Customer Insights", icon: Globe, group: "analytics" },
  { path: "/journey", label: "Customer Journey", icon: GitFork, group: "analytics" },
  { path: "/lab", label: "Лаборатория", icon: FlaskConical, group: "analytics" },
  { path: "/hypotheses", label: "Гипотезы", icon: FlaskConical, group: "system" },
  { path: "/action-log", label: "Журнал действий", icon: ScrollText, group: "system" },
  { path: "/emails", label: "Письма", icon: Mail, group: "system" },
  { path: "/settings/integrations", label: "Доступы", icon: KeyRound, group: "system" },
  { path: "/settings", label: "Настройки", icon: Settings, group: "system" },
];

const GROUPS: Array<{ key: NavItem["group"]; label: string }> = [
  { key: "main", label: "Главное" },
  { key: "marketing", label: "Маркетинг" },
  { key: "direct", label: "Яндекс Директ" },
  { key: "pim", label: "Товар" },
  { key: "market", label: "Яндекс Маркет" },
  { key: "analytics", label: "Аналитика" },
  { key: "system", label: "Система" },
];

function useKillSwitchPoll(): boolean {
  const [active, setActive] = useState(false);
  useEffect(() => {
    let cancelled = false;
    function fetchOnce() {
      api<{ key: string; value: boolean }>("/api/settings/app/kill_switch_active")
        .then((d) => {
          if (!cancelled) setActive(!!d.value);
        })
        .catch(() => {});
    }
    fetchOnce();
    const t = setInterval(fetchOnce, 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);
  return active;
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const killSwitch = useKillSwitchPoll();
  return (
    <aside
      style={{
        background: "var(--sidebar-bg)",
        padding: "20px 0 12px",
        borderRight: "1px solid var(--sidebar-border)",
        width: 240,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        position: "sticky",
        top: 0,
        height: "100vh",
        overflowY: "auto",
      }}
    >
      {/* Brand */}
      <div style={{ padding: "0 20px 20px", borderBottom: "1px solid var(--sidebar-border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: "linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(14,165,233,0.35)",
            }}
          >
            <Sparkles size={16} strokeWidth={2.5} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--sidebar-text)", lineHeight: 1.2 }}>
              Six Reasons Ads
            </div>
            <div style={{ fontSize: 10, color: "var(--sidebar-text-muted)", letterSpacing: "0.05em", textTransform: "uppercase" }}>
              агент Я.Директа
            </div>
          </div>
        </div>
      </div>

      {killSwitch && (
        <NavLink
          to="/settings"
          style={{
            margin: "10px 12px 0",
            padding: "8px 10px",
            background: "#FEE2E2",
            border: "1px solid #FCA5A5",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            gap: 8,
            textDecoration: "none",
            color: "var(--red)",
            fontSize: 11,
            fontWeight: 600,
          }}
          title="Все write-операции в Я.Директ заблокированы — открой настройки чтобы снять"
        >
          <AlertTriangle size={14} strokeWidth={2.5} />
          KILL-SWITCH ON
        </NavLink>
      )}

      {/* Nav */}
      <nav style={{ flex: 1, padding: "12px 8px", overflowY: "auto" }}>
        {GROUPS.map((g) => {
          const items = NAV_ITEMS.filter((i) => i.group === g.key);
          if (items.length === 0) return null;
          return (
            <div key={g.key} style={{ marginBottom: 16 }}>
              <div
                style={{
                  padding: "0 12px 6px",
                  fontSize: 10,
                  color: "var(--sidebar-text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontWeight: 600,
                }}
              >
                {g.label}
              </div>
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === "/"}
                    style={({ isActive }) => ({
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 12px",
                      margin: "2px 0",
                      color: isActive ? "var(--sidebar-active-fg)" : "var(--sidebar-text-muted)",
                      textDecoration: "none",
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 500,
                      borderRadius: 6,
                      background: isActive ? "var(--sidebar-active-bg)" : "transparent",
                      transition: "all 0.12s ease",
                    })}
                  >
                    <Icon size={16} strokeWidth={2} />
                    {item.label}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User */}
      <div
        style={{
          padding: "12px 16px",
          borderTop: "1px solid var(--sidebar-border)",
          fontSize: 11,
          color: "var(--sidebar-text-muted)",
        }}
      >
        <div style={{ marginBottom: 8, color: "var(--sidebar-text)", fontSize: 12, fontWeight: 500 }}>
          {user?.email}
        </div>
        <button
          onClick={logout}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
            width: "100%",
            background: "var(--surface-elevated)",
            border: "1px solid var(--border-default)",
            color: "var(--sidebar-text-muted)",
            padding: "8px 12px",
            borderRadius: 6,
            fontSize: 12,
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          <LogOut size={14} strokeWidth={2} />
          Выйти
        </button>
      </div>
    </aside>
  );
}
